import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    AutoComplete,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Input,
    InputNumber,
    Layout,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Typography,
    message,
} from "antd";

import {
    DeleteOutlined,
    EditOutlined,
    FilePdfOutlined,
    PlusOutlined,
    PrinterOutlined,
    ReloadOutlined,
    SearchOutlined,
    WalletOutlined,
    DollarOutlined,
    TeamOutlined,
    RiseOutlined,
} from "@ant-design/icons";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import Swal from "sweetalert2";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PayrollSlipGenerationPDF from "./PayrollSlipGenerationPDF";
import SalaryAdvance from "./SalaryAdvance";
import { useAcademicYear } from "../../AcademicYearContext";
import api from "../../api/api";
import "./Payroll.css";


const {
    Content,
} = Layout;


const {
    Title,
    Text,
} = Typography;


const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];


const initialFormData = {
    teacher_id: null,
    support_staff_id: null,
    month: "",
    basic_salary: 0,
    night_school_commission: 0,
    salary_advance: 0,
    salary_advance_record: null,
    salary_advance_balance: 0,
    income_tax: 0,
    social_security_tax: 0,
    total_deductions: 0,
};


function Payroll() {
  const [salaryAdvanceBalance, setSalaryAdvanceBalance] = useState(0);
const [salaryAdvanceRecord, setSalaryAdvanceRecord] = useState(null);
const [salaryAdvanceLoading, setSalaryAdvanceLoading] = useState(false);

const {selectedYear} = useAcademicYear();

const [form] = Form.useForm();

const [payrolls, setPayrolls] = useState([]);
const [teachers, setTeachers] = useState([]);
const [supportStaff, setSupportStaff] = useState([]);
const [loading, setLoading] = useState(false);
const [tableLoading, setTableLoading] = useState(false);
const [openModal, setOpenModal] = useState(false);
const [editingPayroll, setEditingPayroll] = useState(null);
const [showSalaryAdvance, setShowSalaryAdvance] = useState(false);

const [filters, setFilters] = useState({
    search: "",
    month: "",
});

const [selectedStaffType, setSelectedStaffType] = useState(null);
const fetchSalaryAdvanceBalance = async ({
    teacherId = null,
    supportStaffId = null,
    month = null,
} = {}) => {
    if (
        !selectedYear?.id ||
        !month ||
        (!teacherId && !supportStaffId)
    ) {
        return;
    }

    setSalaryAdvanceLoading(true);

    try {
        const response = await api.get(
            "/salary-advance-balance/",
            {
                params: {
                    teacher_id:
                        teacherId || undefined,
                    support_staff_id:
                        supportStaffId || undefined,
                    month,
                    academic_year:
                        selectedYear.id,
                },
            }
        );

        console.log(
            "SALARY ADVANCE RESPONSE:",
            response.data
        );

        const balance = Number(
            response.data?.remaining_balance || 0
        );

        const recordId =
            response.data?.salary_advance_id || null;

        setSalaryAdvanceBalance(balance);
        setSalaryAdvanceRecord(recordId);

        form.setFieldsValue({
            salary_advance: balance,
            salary_advance_record: recordId,
        });
    } catch (error) {
        console.error(
            "SALARY ADVANCE ERROR:",
            error.response?.data || error
        );

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        form.setFieldsValue({
            salary_advance: 0,
            salary_advance_record: null,
        });
    } finally {
        setSalaryAdvanceLoading(false);
    }
};
    /*
    |--------------------------------------------------------------------------
    | FETCH PAYROLLS
    |--------------------------------------------------------------------------
    */

    const fetchPayrolls = async () => {

        if (!selectedYear?.id) {

            return;

        }


        setTableLoading(true);


        try {

            const response = await api.get(
                "/payrolls/",
                {
                    params: {
                        academic_year:
                            selectedYear.id,
                    },
                }
            );


            const data =
                Array.isArray(response.data)

                    ? response.data

                    : response.data?.results || [];


            setPayrolls(data);

        } catch (error) {

            console.error(
                "PAYROLL FETCH ERROR:",
                error.response?.data || error
            );


            message.error(
                "Failed to load payroll records."
            );

        } finally {

            setTableLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | FETCH TEACHERS
    |--------------------------------------------------------------------------
    */

    const fetchTeachers = async () => {

        try {

            const response = await api.get(
                "/api/users/teacher-data/"
            );


            const data =
                Array.isArray(response.data)

                    ? response.data

                    : response.data?.results || [];


            setTeachers(data);

        } catch (error) {

            console.error(
                "TEACHERS FETCH ERROR:",
                error
            );


            message.error(
                "Failed to load teachers."
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | FETCH SUPPORT STAFF
    |--------------------------------------------------------------------------
    */

    const fetchSupportStaff = async () => {

        try {

            const response = await api.get(
                "/supportstaff/"
            );


            const data =
                Array.isArray(response.data)

                    ? response.data

                    : response.data?.results || [];


            setSupportStaff(data);

        } catch (error) {

            console.error(
                "SUPPORT STAFF FETCH ERROR:",
                error
            );


            message.error(
                "Failed to load support staff."
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | EFFECTS
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (selectedYear?.id) {

            fetchPayrolls();

        }

    }, [
        selectedYear?.id,
    ]);


    useEffect(() => {

        fetchTeachers();

        fetchSupportStaff();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | FORMAT MONEY
    |--------------------------------------------------------------------------
    */

    const formatMoney = (
        value
    ) => {

        const number =
            parseFloat(value || 0);


        return `$${number.toLocaleString(
            "en-US",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        )}`;

    };


    /*
    |--------------------------------------------------------------------------
    | STAFF NAME
    |--------------------------------------------------------------------------
    */

    const getStaffName = (
        payroll
    ) => {

        return (

            payroll.teacher?.full_name ||

            payroll.support_staff?.full_name ||

            "Unknown Staff"

        );

    };


    /*
    |--------------------------------------------------------------------------
    | OPEN CREATE MODAL
    |--------------------------------------------------------------------------
    */

    const openCreateModal = () => {

        setEditingPayroll(null);

        setSelectedStaffType(null);

        form.resetFields();

        form.setFieldsValue({

            ...initialFormData,

        });

        setOpenModal(true);

    };


    /*
    |--------------------------------------------------------------------------
    | OPEN EDIT MODAL
    |--------------------------------------------------------------------------
    */

    const openEditModal = (
        payroll
    ) => {

        setEditingPayroll(payroll);


        const teacherId =
            payroll.teacher?.id || null;


        const supportStaffId =
            payroll.support_staff?.id || null;


        setSelectedStaffType(

            teacherId

                ? "teacher"

                : supportStaffId

                    ? "support_staff"

                    : null

        );


        form.setFieldsValue({

            teacher_id:
                teacherId,

            support_staff_id:
                supportStaffId,

            month:
                payroll.month || "",

            basic_salary:
                Number(
                    payroll.basic_salary || 0
                ),

            night_school_commission:
                Number(
                    payroll.night_school_commission || 0
                ),

            salary_advance:
                Number(
                    payroll.salary_advance || 0
                ),

            income_tax:
                Number(
                    payroll.income_tax || 0
                ),

            social_security_tax:
                Number(
                    payroll.social_security_tax || 0
                ),

            total_deductions:
                Number(
                    payroll.total_deductions || 0
                ),

        });


        setOpenModal(true);

    };


    /*
    |--------------------------------------------------------------------------
    | CLOSE MODAL
    |--------------------------------------------------------------------------
    */

    const closeModal = () => {

        setOpenModal(false);

        setEditingPayroll(null);

        setSelectedStaffType(null);

        form.resetFields();

    };


    /*
    |--------------------------------------------------------------------------
    | GET TEACHER SALARY
    |--------------------------------------------------------------------------
    */

    const fetchTeacherSalary = async (
        teacherId
    ) => {

        if (!teacherId) {

            return;

        }


        try {

            const response = await api.get(
                `/teacher-salary/${teacherId}/`
            );


            form.setFieldValue(
                "basic_salary",
                Number(
                    response.data.salary || 0
                )
            );

        } catch (error) {

            console.error(
                "SALARY FETCH ERROR:",
                error
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | TEACHER CHANGE
    |--------------------------------------------------------------------------
    */
const handleTeacherChange = async (teacherId) => {
    setSelectedStaffType(
        teacherId ? "teacher" : null
    );

    const currentMonth =
        form.getFieldValue("month");

    form.setFieldsValue({
        teacher_id: teacherId,
        support_staff_id: null,
        salary_advance: 0,
        salary_advance_record: null,
    });

    setSalaryAdvanceBalance(0);
    setSalaryAdvanceRecord(null);

    const teacher = teachers.find(
        (item) => item.id === teacherId
    );

    if (teacher) {
        form.setFieldsValue({
            basic_salary: Number(
                teacher.salary || 0
            ),
        });
    }

    if (
        teacherId &&
        currentMonth
    ) {
        await fetchSalaryAdvanceBalance({
            teacherId,
            month: currentMonth,
        });
    }
};

    /*
    |--------------------------------------------------------------------------
    | SUPPORT STAFF CHANGE
    |--------------------------------------------------------------------------
    */
const handleSupportStaffChange = async (staffId) => {
    setSelectedStaffType(staffId ? "support_staff" : null);

    const currentMonth = form.getFieldValue("month");

    const staff = supportStaff.find((item) => item.id === staffId);

    form.setFieldsValue({
        support_staff_id: staffId || null,
        teacher_id: null,
        basic_salary: Number(staff?.salary || 0),
        salary_advance: 0,
        salary_advance_record: null,
    });

    if (staffId && currentMonth) {
        await fetchSalaryAdvanceBalance({
            supportStaffId: staffId,
            month: currentMonth,
        });
    }
};

// Handle Month Change:
const handleMonthChange = async (month) => {
    const teacherId = form.getFieldValue("teacher_id");
    const supportStaffId = form.getFieldValue("support_staff_id");

    if (teacherId) {
        await fetchSalaryAdvanceBalance({
            teacherId,
            month,
        });
    }

    if (supportStaffId) {
        await fetchSalaryAdvanceBalance({
            supportStaffId,
            month,
        });
    }
};

    /*
    |--------------------------------------------------------------------------
    | SUBMIT PAYROLL
    |--------------------------------------------------------------------------
    */
const handleSubmit = async (values) => {
    if (!values.teacher_id && !values.support_staff_id) {
        message.error("Please select a teacher or support staff.");
        return;
    }

    if (!selectedYear?.id) {
        message.error("Please select an academic year.");
        return;
    }
const deduction = Number(
    values.salary_advance || 0
);

const outstandingBalance = Number(
    salaryAdvanceBalance || 0
);

if (deduction > outstandingBalance) {
    message.error(
        "Salary advance deduction cannot be greater than the outstanding balance."
    );

    return;
}

    setLoading(true);

    try {
  const payload = {
    teacher_id: values.teacher_id || null,
    support_staff_id: values.support_staff_id || null,
    month: values.month,
    basic_salary: Number(values.basic_salary || 0),
    night_school_commission: Number(values.night_school_commission || 0),
    salary_advance: deduction,
    salary_advance_record: values.salary_advance_record || salaryAdvanceRecord || null,
    income_tax: Number(values.income_tax || 0),
    social_security_tax: Number(values.social_security_tax || 0),
};

        const response = editingPayroll
            ? await api.put(
                `/payrolls/${editingPayroll.id}/`,
                payload
            )
            : await api.post(
                "/payrolls/",
                payload
            );

        if (editingPayroll) {
            setPayrolls((previous) =>
                previous.map((item) =>
                    item.id === editingPayroll.id
                        ? response.data
                        : item
                )
            );

            message.success(
                "Payroll updated successfully."
            );
        } else {
            setPayrolls((previous) => [
                response.data,
                ...previous,
            ]);

            message.success(
                "Payroll created successfully."
            );
        }

        closeModal();
    } catch (error) {
        console.error(
            "PAYROLL SUBMIT ERROR:",
            error.response?.data || error
        );

        Swal.fire({
            icon: "error",
            title: "Payroll Error",
            text:
                typeof error.response?.data === "object"
                    ? JSON.stringify(
                        error.response.data
                    )
                    : (
                        error.response?.data ||
                        "Unable to save payroll."
                    ),
        });
    } finally {
        setLoading(false);
    }
};
    /*
    |--------------------------------------------------------------------------
    | DELETE PAYROLL
    |--------------------------------------------------------------------------
    */

    const handleDelete = async (
        id
    ) => {

        const confirmation =
            await Swal.fire({

                title:
                    "Delete Payroll?",

                text:
                    "This payroll record will be permanently deleted.",

                icon:
                    "warning",

                showCancelButton:
                    true,

                confirmButtonText:
                    "Delete",

                confirmButtonColor:
                    "#f97316",

            });


        if (
            !confirmation.isConfirmed
        ) {

            return;

        }


        try {

            await api.delete(
                `/payrolls/${id}/`
            );


            setPayrolls(
                (previous) =>

                    previous.filter(
                        (item) =>
                            item.id !== id
                    )

            );


            message.success(
                "Payroll deleted successfully."
            );

        } catch (error) {

            console.error(
                error
            );


            message.error(
                "Failed to delete payroll."
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | PRINT PAYROLL
    |--------------------------------------------------------------------------
    */

    const handlePrint = (
        payroll
    ) => {

        const newWindow =
            window.open(
                "",
                "_blank"
            );


        if (!newWindow) {

            return;

        }


        const staffName =
            getStaffName(payroll);


        newWindow.document.write(`

            <html>

                <head>

                    <title>
                        Payroll - ${staffName}
                    </title>

                    <style>

                        body {
                            font-family: Arial, sans-serif;
                            padding: 30px;
                            color: #111;
                        }

                        h1 {
                            text-align: center;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 25px;
                        }

                        th,
                        td {
                            border: 1px solid #ddd;
                            padding: 12px;
                            text-align: left;
                        }

                        th {
                            background: #111;
                            color: white;
                        }

                        .total {
                            font-weight: bold;
                            font-size: 18px;
                        }

                    </style>

                </head>

                <body>

                    <h1>
                        Payroll Slip
                    </h1>

                    <p>
                        <strong>Staff:</strong>
                        ${staffName}
                    </p>

                    <p>
                        <strong>Month:</strong>
                        ${payroll.month}
                    </p>

                    <p>
                        <strong>Academic Year:</strong>
                        ${payroll.academic_year?.name || ""}
                    </p>

                    <table>

                        <tr>
                            <th>Basic Salary</th>
                            <td>
                                ${formatMoney(
                                    payroll.basic_salary
                                )}
                            </td>
                        </tr>

                        <tr>
                            <th>Bonus / Compensation</th>
                            <td>
                                ${formatMoney(
                                    payroll.night_school_commission
                                )}
                            </td>
                        </tr>

                        <tr>
                            <th>Salary Advance</th>
                            <td>
                                ${formatMoney(
                                    payroll.salary_advance
                                )}
                            </td>
                        </tr>

                        <tr>
                            <th>Income Tax</th>
                            <td>
                                ${formatMoney(
                                    payroll.income_tax
                                )}
                            </td>
                        </tr>

                        <tr>
                            <th>Social Security</th>
                            <td>
                                ${formatMoney(
                                    payroll.social_security_tax
                                )}
                            </td>
                        </tr>

                        <tr>
                            <th>Total Deductions</th>
                            <td>
                                ${formatMoney(
                                    payroll.total_deductions
                                )}
                            </td>
                        </tr>

                        <tr class="total">
                            <th>Total Paid</th>
                            <td>
                                ${formatMoney(
                                    payroll.total_paid
                                )}
                            </td>
                        </tr>

                    </table>

                </body>

            </html>

        `);


        newWindow.document.close();

        newWindow.print();

    };


    /*
    |--------------------------------------------------------------------------
    | FILTERED PAYROLLS
    |--------------------------------------------------------------------------
    */

    const filteredPayrolls =
        useMemo(() => {

            return payrolls.filter(
                (payroll) => {

                    const staffName =
                        getStaffName(payroll);


                    const matchesSearch =
                        !filters.search ||

                        staffName
                            .toLowerCase()
                            .includes(
                                filters.search
                                    .toLowerCase()
                            );


                    const matchesMonth =
                        !filters.month ||

                        payroll.month
                            ?.toLowerCase() ===
                        filters.month
                            .toLowerCase();


                    return (

                        matchesSearch &&

                        matchesMonth

                    );

                }
            );

        }, [
            payrolls,
            filters,
        ]);


    /*
    |--------------------------------------------------------------------------
    | STATISTICS
    |--------------------------------------------------------------------------
    */

    const statistics =
        useMemo(() => {

            const totalPayroll =
                filteredPayrolls.reduce(
                    (
                        total,
                        item
                    ) =>

                        total +
                        Number(
                            item.total_paid || 0
                        ),

                    0
                );


            const totalDeductions =
                filteredPayrolls.reduce(
                    (
                        total,
                        item
                    ) =>

                        total +
                        Number(
                            item.total_deductions || 0
                        ),

                    0
                );


            const totalAdvances =
                filteredPayrolls.reduce(
                    (
                        total,
                        item
                    ) =>

                        total +
                        Number(
                            item.salary_advance || 0
                        ),

                    0
                );


            return {

                totalPayroll,

                totalDeductions,

                totalAdvances,

                staffCount:
                    filteredPayrolls.length,

            };

        }, [
            filteredPayrolls,
        ]);


    /*
    |--------------------------------------------------------------------------
    | CHART DATA
    |--------------------------------------------------------------------------
    */

    const chartData =
        useMemo(() => {

            const grouped =
                filteredPayrolls.reduce(
                    (
                        accumulator,
                        item
                    ) => {

                        const key =
                            item.month;


                        if (
                            !accumulator[key]
                        ) {

                            accumulator[key] = {

                                month:
                                    key,

                                total:
                                    0,

                            };

                        }


                        accumulator[key].total +=
                            Number(
                                item.total_paid || 0
                            );


                        return accumulator;

                    },

                    {}
                );


            return Object.values(
                grouped
            );

        }, [
            filteredPayrolls,
        ]);


    /*
    |--------------------------------------------------------------------------
    | TABLE COLUMNS
    |--------------------------------------------------------------------------
    */

    const columns = [

        {

            title:
                "Staff Name",

            key:
                "staff",

            fixed:
                "left",

            width:
                220,

            render:
                (_, record) => (

                    <div className="staff-cell">

                        <div className="staff-avatar">

                            {getStaffName(
                                record
                            )
                                .charAt(0)
                                .toUpperCase()}

                        </div>

                        <div>

                            <Text
                                strong
                            >

                                {getStaffName(
                                    record
                                )}

                            </Text>

                            <br />

                            <Text
                                type="secondary"
                                className="staff-type"
                            >

                                {record.teacher
                                    ? "Teacher"
                                    : "Support Staff"}

                            </Text>

                        </div>

                    </div>

                ),

        },


        {

            title:
                "Month",

            dataIndex:
                "month",

            key:
                "month",

            width:
                120,

            render:
                (month) => (

                    <Tag
                        color="orange"
                    >

                        {month}

                    </Tag>

                ),

        },


        {

            title:
                "Academic Year",

            key:
                "academic_year",

            width:
                150,

            render:
                (_, record) =>

                    record.academic_year?.name || "-",

        },


        {

            title:
                "Basic Salary",

            dataIndex:
                "basic_salary",

            key:
                "basic_salary",

            width:
                140,

            render:
                (value) =>

                    formatMoney(
                        value
                    ),

        },


        {

            title:
                "Bonus",

            dataIndex:
                "night_school_commission",

            key:
                "bonus",

            width:
                140,

            render:
                (value) =>

                    formatMoney(
                        value
                    ),

        },


        {

            title:
                "Deductions",

            dataIndex:
                "total_deductions",

            key:
                "deductions",

            width:
                140,

            render:
                (value) => (

                    <Text
                        type="danger"
                        strong
                    >

                        {formatMoney(
                            value
                        )}

                    </Text>

                ),

        },


        {

            title:
                "Advance",

            dataIndex:
                "salary_advance",

            key:
                "advance",

            width:
                140,

            render:
                (value) => (

                    <Text
                        type="danger"
                        strong
                    >

                        {formatMoney(
                            value
                        )}

                    </Text>

                ),

        },


        {

            title:
                "Income Tax",

            dataIndex:
                "income_tax",

            key:
                "income_tax",

            width:
                140,

            render:
                (value) =>

                    formatMoney(
                        value
                    ),

        },


        {

            title:
                "Social Security",

            dataIndex:
                "social_security_tax",

            key:
                "social_security",

            width:
                150,

            render:
                (value) =>

                    formatMoney(
                        value
                    ),

        },


        {

            title:
                "Total Paid",

            dataIndex:
                "total_paid",

            key:
                "total_paid",

            width:
                150,

            render:
                (value) => (

                    <Text
                        strong
                        className="total-paid"
                    >

                        {formatMoney(
                            value
                        )}

                    </Text>

                ),

        },


        {

            title:
                "Actions",

            key:
                "actions",

            fixed:
                "right",

            width:
                160,

            render:
                (_, record) => (

                    <Space>

                        <Button

                            type="text"

                            icon={
                                <EditOutlined />
                            }

                            onClick={() =>
                                openEditModal(
                                    record
                                )
                            }

                        />

                        <Button

                            type="text"

                            danger

                            icon={
                                <DeleteOutlined />
                            }

                            onClick={() =>
                                handleDelete(
                                    record.id
                                )
                            }

                        />

                        <Button

                            type="text"

                            icon={
                                <PrinterOutlined />
                            }

                            onClick={() =>
                                handlePrint(
                                    record
                                )
                            }

                        />

                    </Space>

                ),

        },

    ];


    return (

        <Layout
            className="payroll-page"
        >

            <Content
                className="payroll-content"
            >

                {/* HEADER */}

                <div
                    className="payroll-header"
                >

                    <div>

                        <Text
                            className="eyebrow"
                        >

                            FINANCE / PAYROLL

                        </Text>


                        <Title
                            level={2}
                            className="page-title"
                        >

                            Staff Payroll Management

                        </Title>


                        <Text
                            type="secondary"
                        >

                            Manage staff compensation, deductions,
                            salary advances and payroll reporting.

                        </Text>

                    </div>


                    <div
                        className="year-badge"
                    >

                        <Text>
                            Academic Year
                        </Text>

                        <strong>
                            {selectedYear?.name || "Not Selected"}
                        </strong>

                    </div>

                </div>


                {/* STATISTICS */}

                <Row
                    gutter={[
                        16,
                        16,
                    ]}
                    className="stats-row"
                >

                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >

                        <Card
                            className="stat-card orange-card"
                        >

                            <Statistic

                                title="Total Payroll"

                                value={
                                    statistics.totalPayroll
                                }

                                precision={2}

                                prefix="$"

                            />

                            <DollarOutlined
                                className="stat-icon"
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >

                        <Card
                            className="stat-card dark-card"
                        >

                            <Statistic

                                title="Staff Records"

                                value={
                                    statistics.staffCount
                                }

                                prefix={
                                    <TeamOutlined />
                                }

                            />

                            <TeamOutlined
                                className="stat-icon"
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >

                        <Card
                            className="stat-card light-card"
                        >

                            <Statistic

                                title="Total Deductions"

                                value={
                                    statistics.totalDeductions
                                }

                                precision={2}

                                prefix="$"

                            />

                            <RiseOutlined
                                className="stat-icon"
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >

                        <Card
                            className="stat-card black-card"
                        >

                            <Statistic

                                title="Salary Advances"

                                value={
                                    statistics.totalAdvances
                                }

                                precision={2}

                                prefix="$"

                            />

                            <WalletOutlined
                                className="stat-icon"
                            />

                        </Card>

                    </Col>

                </Row>


                {/* SALARY ADVANCE */}

                {showSalaryAdvance ? (

                    <Card>

                        <Button

                            icon={
                                <ReloadOutlined />
                            }

                            onClick={() =>
                                setShowSalaryAdvance(
                                    false
                                )
                            }

                            className="back-button"

                        >

                            Back To Payroll

                        </Button>


                        <SalaryAdvance />

                    </Card>

                ) : (

                    <>

                        {/* TOOLBAR */}

                        <Card
                            className="toolbar-card"
                        >

                            <Row
                                gutter={[
                                    12,
                                    12,
                                ]}
                                align="middle"
                            >

                                <Col
                                    xs={24}
                                    md={8}
                                >

                                    <Input

                                        size="large"

                                        prefix={
                                            <SearchOutlined />
                                        }

                                        placeholder="Search staff name..."

                                        value={
                                            filters.search
                                        }

                                        onChange={(
                                            event
                                        ) =>

                                            setFilters(
                                                (
                                                    previous
                                                ) => ({

                                                    ...previous,

                                                    search:
                                                        event
                                                            .target
                                                            .value,

                                                })
                                            )

                                        }

                                        allowClear

                                    />

                                </Col>


                                <Col
                                    xs={24}
                                    sm={12}
                                    md={5}
                                >

                                    <Select

                                        size="large"

                                        placeholder="Filter by month"

                                        value={
                                            filters.month ||
                                            undefined
                                        }

                                        onChange={(
                                            value
                                        ) =>

                                            setFilters(
                                                (
                                                    previous
                                                ) => ({

                                                    ...previous,

                                                    month:
                                                        value ||
                                                        "",

                                                })
                                            )

                                        }

                                        allowClear

                                        options={
                                            MONTHS.map(
                                                (
                                                    month
                                                ) => ({

                                                    label:
                                                        month,

                                                    value:
                                                        month,

                                                })
                                            )
                                        }

                                        style={{
                                            width:
                                                "100%",
                                        }}

                                    />

                                </Col>


                                <Col
                                    xs={24}
                                    sm={12}
                                    md={11}
                                >

                                    <Space
                                        wrap
                                        className="toolbar-actions"
                                    >

                                        <Button

                                            icon={
                                                <WalletOutlined />
                                            }

                                            onClick={() =>
                                                setShowSalaryAdvance(
                                                    true
                                                )
                                            }

                                        >

                                            Salary Advances

                                        </Button>


                                        <Button

                                            type="primary"

                                            icon={
                                                <PlusOutlined />
                                            }

                                            onClick={
                                                openCreateModal
                                            }

                                        >

                                            Create Payroll

                                        </Button>


                                        <PDFDownloadLink

                                            document={

                                                <PayrollSlipGenerationPDF

                                                    payrolls={
                                                        filteredPayrolls
                                                    }

                                                    month={
                                                        filters.month
                                                    }

                                                    academicYear={
                                                        selectedYear?.name
                                                    }

                                                />

                                            }

                                            fileName={

                                                `Payslips_${

                                                    filters.month ||
                                                    "All_Months"

                                                }_${

                                                    selectedYear?.name ||
                                                    "Academic_Year"

                                                }.pdf`

                                            }

                                        >

                                            {({
                                                loading,
                                            }) => (

                                                <Button

                                                    icon={
                                                        <FilePdfOutlined />
                                                    }

                                                    danger

                                                >

                                                    {loading

                                                        ? "Generating..."

                                                        : "Download PDF"}

                                                </Button>

                                            )}

                                        </PDFDownloadLink>


                                    </Space>

                                </Col>

                            </Row>

                        </Card>


                        {/* TABLE */}

                        <Card
                            className="table-card"
                        >

                            <Table

                                rowKey="id"

                                loading={
                                    tableLoading
                                }

                                columns={
                                    columns
                                }

                                dataSource={
                                    filteredPayrolls
                                }

                                scroll={{
                                    x:
                                        1650,
                                }}

                                pagination={{
                                    pageSize:
                                        10,

                                    showSizeChanger:
                                        true,

                                    pageSizeOptions:
                                        [
                                            "10",
                                            "20",
                                            "50",
                                            "100",
                                        ],

                                    showTotal:
                                        (
                                            total
                                        ) =>

                                            `${total} payroll records`,

                                }}

                                locale={{

                                    emptyText: (

                                        <Empty

                                            description={
                                                selectedYear?.id

                                                    ? "No payroll records found"

                                                    : "Please select an academic year"

                                            }

                                        />

                                    ),

                                }}

                            />

                        </Card>


                        {/* CHART */}

                        <Card
                            className="chart-card"
                        >

                            <div
                                className="section-heading"
                            >

                                <div>

                                    <Title
                                        level={4}
                                    >

                                        Monthly Payroll Summary

                                    </Title>

                                    <Text
                                        type="secondary"
                                    >

                                        Total payroll paid by month

                                    </Text>

                                </div>

                            </div>


                            <div
                                className="chart-wrapper"
                            >

                                <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                >

                                    <BarChart
                                        data={
                                            chartData
                                        }
                                    >

                                        <XAxis
                                            dataKey="month"
                                        />

                                        <YAxis />

                                        <Tooltip />

                                        <Bar
                                            dataKey="total"
                                            fill="#f97316"
                                            radius={[
                                                8,
                                                8,
                                                0,
                                                0,
                                            ]}
                                        />

                                    </BarChart>

                                </ResponsiveContainer>

                            </div>

                        </Card>

                    </>

                )}


                {/* CREATE / EDIT MODAL */}

                <Modal

                    open={
                        openModal
                    }

                    title={

                        <div
                            className="modal-title"
                        >

                            <div
                                className="modal-title-icon"
                            >

                                <DollarOutlined />

                            </div>


                            <div>

                                <Title
                                    level={4}
                                >

                                    {editingPayroll

                                        ? "Edit Payroll"

                                        : "Create Payroll"}

                                </Title>

                                <Text
                                    type="secondary"
                                >

                                    {selectedYear?.name}

                                </Text>

                            </div>

                        </div>

                    }

                    onCancel={
                        closeModal
                    }

                    footer={null}

                    width={
                        850
                    }

                    centered

                    destroyOnClose

                >

                    <Alert

                        message="Select only one staff member"

                        description="A payroll record can belong to either a Teacher or Support Staff, not both."

                        type="info"

                        showIcon

                        style={{
                            marginBottom:
                                24,
                        }}

                    />


                    <Form

                        form={
                            form
                        }

                        layout="vertical"

                        onFinish={
                            handleSubmit
                        }

                    >

                        <Row
                            gutter={[
                                16,
                                0,
                            ]}
                        >

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Teacher"
                                    name="teacher_id"
                                >

                                    <Select

                                        showSearch

                                        allowClear

                                        placeholder="Select teacher"

                                        disabled={
                                            selectedStaffType ===
                                            "support_staff"
                                        }

                                        optionFilterProp="label"

                                        onChange={
                                            handleTeacherChange
                                        }

                                        options={

                                            teachers.map(
                                                (
                                                    teacher
                                                ) => ({

                                                    label:
                                                        teacher.full_name,

                                                    value:
                                                        teacher.id,

                                                })
                                            )

                                        }

                                    />

                                </Form.Item>

                            </Col>


                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Support Staff"
                                    name="support_staff_id"
                                >

                                    <Select

                                        showSearch

                                        allowClear

                                        placeholder="Select support staff"

                                        disabled={
                                            selectedStaffType ===
                                            "teacher"
                                        }

                                        optionFilterProp="label"

                                        onChange={
                                            handleSupportStaffChange
                                        }

                                        options={

                                            supportStaff.map(
                                                (
                                                    staff
                                                ) => ({

                                                    label:
                                                        staff.full_name,

                                                    value:
                                                        staff.id,

                                                })
                                            )

                                        }

                                    />

                                </Form.Item>

                            </Col>


                            <Col
                                xs={24}
                                md={12}
                            >

                               <Form.Item
    label="Payroll Month"
    name="month"
    rules={[
        {
            required: true,
            message: "Please select a month.",
        },
    ]}
>
<Select
    onChange={handleMonthChange}
    options={MONTHS.map((month) => ({
        label: month,
        value: month,
    }))}
/>
</Form.Item>

                            </Col>


                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item

                                    label="Basic Salary"

                                    name="basic_salary"

                                >

                                    <InputNumber

                                        style={{
                                            width:
                                                "100%",
                                        }}

                                        prefix="$"

                                        readOnly

                                        min={
                                            0
                                        }

                                    />

                                </Form.Item>

                            </Col>


                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item

                                    label="Bonus / Compensation"

                                    name="night_school_commission"

                                >

                                    <InputNumber

                                        style={{
                                            width:
                                                "100%",
                                        }}

                                        prefix="$"

                                        min={
                                            0
                                        }

                                    />

                                </Form.Item>

                            </Col>


                            <Col
                                xs={24}
                                md={12}
                            >

                            <Form.Item
    label="Salary Advance Deduction"
    name="salary_advance"
    extra={
        salaryAdvanceBalance > 0
            ? `Outstanding balance: $${salaryAdvanceBalance.toFixed(2)}`
            : "No outstanding salary advance"
    }
>
    <InputNumber
        style={{
            width: "100%",
        }}
        min={0}
        precision={2}
        prefix="$"
        disabled={salaryAdvanceLoading}
        onChange={(value) => {
            const deduction = Number(value || 0);

            if (deduction > salaryAdvanceBalance) {
                message.warning(
                    "The deduction cannot be greater than the outstanding salary advance."
                );
            }
        }}
    />
</Form.Item>
<Form.Item
    name="salary_advance_record"
    hidden
>
    <Input />
</Form.Item>

                            </Col>


                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item

                                    label="Income Tax"

                                    name="income_tax"

                                >

                                    <InputNumber

                                        style={{
                                            width:
                                                "100%",
                                        }}

                                        prefix="$"

                                        min={
                                            0
                                        }

                                    />

                                </Form.Item>

                            </Col>


                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item

                                    label="Social Security Tax"

                                    name="social_security_tax"

                                >

                                    <InputNumber

                                        style={{
                                            width:
                                                "100%",
                                        }}

                                        prefix="$"

                                        min={
                                            0
                                        }

                                    />

                                </Form.Item>

                            </Col>

                        </Row>


                        <div
                            className="modal-footer"
                        >

                            <Button
                                onClick={
                                    closeModal
                                }
                            >

                                Cancel

                            </Button>


                            <Button

                                type="primary"

                                htmlType="submit"

                                loading={
                                    loading
                                }

                            >

                                {editingPayroll

                                    ? "Update Payroll"

                                    : "Create Payroll"}

                            </Button>

                        </div>

                    </Form>

                </Modal>

            </Content>

        </Layout>

    );

}


export default Payroll;





// import React, { useEffect, useMemo, useState } from "react";

// import {
//     Alert,
//     AutoComplete,
//     Button,
//     Card,
//     Col,
//     Empty,
//     Form,
//     Input,
//     InputNumber,
//     Layout,
//     Modal,
//     Row,
//     Select,
//     Space,
//     Statistic,
//     Table,
//     Tag,
//     Typography,
//     message,
// } from "antd";

// import {
//     DeleteOutlined,
//     EditOutlined,
//     FilePdfOutlined,
//     PlusOutlined,
//     PrinterOutlined,
//     ReloadOutlined,
//     SearchOutlined,
//     WalletOutlined,
//     DollarOutlined,
//     TeamOutlined,
//     RiseOutlined,
// } from "@ant-design/icons";

// import {
//     BarChart,
//     Bar,
//     XAxis,
//     YAxis,
//     Tooltip,
//     ResponsiveContainer,
// } from "recharts";
// import Swal from "sweetalert2";
// import { PDFDownloadLink } from "@react-pdf/renderer";
// import PayrollSlipGenerationPDF from "./PayrollSlipGenerationPDF";
// import SalaryAdvance from "./SalaryAdvance";
// import { useAcademicYear } from "../../AcademicYearContext";
// import api from "../../api/api";
// import "./Payroll.css";


// const {
//     Content,
// } = Layout;


// const {
//     Title,
//     Text,
// } = Typography;


// const MONTHS = [
//     "January",
//     "February",
//     "March",
//     "April",
//     "May",
//     "June",
//     "July",
//     "August",
//     "September",
//     "October",
//     "November",
//     "December",
// ];


// const initialFormData = {
//     teacher_id: null,
//     support_staff_id: null,
//     month: "",
//     basic_salary: 0,
//     night_school_commission: 0,
//     salary_advance: 0,
//     income_tax: 0,
//     social_security_tax: 0,
//     total_deductions: 0,
// };


// function Payroll() {

//     const {
//         selectedYear,
//     } = useAcademicYear();


//     const [
//         form,
//     ] = Form.useForm();


//     const [
//         payrolls,
//         setPayrolls,
//     ] = useState([]);


//     const [
//         teachers,
//         setTeachers,
//     ] = useState([]);


//     const [
//         supportStaff,
//         setSupportStaff,
//     ] = useState([]);


//     const [
//         loading,
//         setLoading,
//     ] = useState(false);


//     const [
//         tableLoading,
//         setTableLoading,
//     ] = useState(false);


//     const [
//         openModal,
//         setOpenModal,
//     ] = useState(false);


//     const [
//         editingPayroll,
//         setEditingPayroll,
//     ] = useState(null);


//     const [
//         showSalaryAdvance,
//         setShowSalaryAdvance,
//     ] = useState(false);


//     const [
//         filters,
//         setFilters,
//     ] = useState({
//         search: "",
//         month: "",
//     });


//     const [
//         selectedStaffType,
//         setSelectedStaffType,
//     ] = useState(null);


//     /*
//     |--------------------------------------------------------------------------
//     | FETCH PAYROLLS
//     |--------------------------------------------------------------------------
//     */

//     const fetchPayrolls = async () => {

//         if (!selectedYear?.id) {

//             return;

//         }


//         setTableLoading(true);


//         try {

//             const response = await api.get(
//                 "/payrolls/",
//                 {
//                     params: {
//                         academic_year:
//                             selectedYear.id,
//                     },
//                 }
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setPayrolls(data);

//         } catch (error) {

//             console.error(
//                 "PAYROLL FETCH ERROR:",
//                 error.response?.data || error
//             );


//             message.error(
//                 "Failed to load payroll records."
//             );

//         } finally {

//             setTableLoading(false);

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FETCH TEACHERS
//     |--------------------------------------------------------------------------
//     */

//     const fetchTeachers = async () => {

//         try {

//             const response = await api.get(
//                 "/api/users/teacher-data/"
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setTeachers(data);

//         } catch (error) {

//             console.error(
//                 "TEACHERS FETCH ERROR:",
//                 error
//             );


//             message.error(
//                 "Failed to load teachers."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FETCH SUPPORT STAFF
//     |--------------------------------------------------------------------------
//     */

//     const fetchSupportStaff = async () => {

//         try {

//             const response = await api.get(
//                 "/supportstaff/"
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setSupportStaff(data);

//         } catch (error) {

//             console.error(
//                 "SUPPORT STAFF FETCH ERROR:",
//                 error
//             );


//             message.error(
//                 "Failed to load support staff."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | EFFECTS
//     |--------------------------------------------------------------------------
//     */

//     useEffect(() => {

//         if (selectedYear?.id) {

//             fetchPayrolls();

//         }

//     }, [
//         selectedYear?.id,
//     ]);


//     useEffect(() => {

//         fetchTeachers();

//         fetchSupportStaff();

//     }, []);


//     /*
//     |--------------------------------------------------------------------------
//     | FORMAT MONEY
//     |--------------------------------------------------------------------------
//     */

//     const formatMoney = (
//         value
//     ) => {

//         const number =
//             parseFloat(value || 0);


//         return `$${number.toLocaleString(
//             "en-US",
//             {
//                 minimumFractionDigits: 2,
//                 maximumFractionDigits: 2,
//             }
//         )}`;

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | STAFF NAME
//     |--------------------------------------------------------------------------
//     */

//     const getStaffName = (
//         payroll
//     ) => {

//         return (

//             payroll.teacher?.full_name ||

//             payroll.support_staff?.full_name ||

//             "Unknown Staff"

//         );

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | OPEN CREATE MODAL
//     |--------------------------------------------------------------------------
//     */

//     const openCreateModal = () => {

//         setEditingPayroll(null);

//         setSelectedStaffType(null);

//         form.resetFields();

//         form.setFieldsValue({

//             ...initialFormData,

//         });

//         setOpenModal(true);

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | OPEN EDIT MODAL
//     |--------------------------------------------------------------------------
//     */

//     const openEditModal = (
//         payroll
//     ) => {

//         setEditingPayroll(payroll);


//         const teacherId =
//             payroll.teacher?.id || null;


//         const supportStaffId =
//             payroll.support_staff?.id || null;


//         setSelectedStaffType(

//             teacherId

//                 ? "teacher"

//                 : supportStaffId

//                     ? "support_staff"

//                     : null

//         );


//         form.setFieldsValue({

//             teacher_id:
//                 teacherId,

//             support_staff_id:
//                 supportStaffId,

//             month:
//                 payroll.month || "",

//             basic_salary:
//                 Number(
//                     payroll.basic_salary || 0
//                 ),

//             night_school_commission:
//                 Number(
//                     payroll.night_school_commission || 0
//                 ),

//             salary_advance:
//                 Number(
//                     payroll.salary_advance || 0
//                 ),

//             income_tax:
//                 Number(
//                     payroll.income_tax || 0
//                 ),

//             social_security_tax:
//                 Number(
//                     payroll.social_security_tax || 0
//                 ),

//             total_deductions:
//                 Number(
//                     payroll.total_deductions || 0
//                 ),

//         });


//         setOpenModal(true);

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | CLOSE MODAL
//     |--------------------------------------------------------------------------
//     */

//     const closeModal = () => {

//         setOpenModal(false);

//         setEditingPayroll(null);

//         setSelectedStaffType(null);

//         form.resetFields();

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | GET TEACHER SALARY
//     |--------------------------------------------------------------------------
//     */

//     const fetchTeacherSalary = async (
//         teacherId
//     ) => {

//         if (!teacherId) {

//             return;

//         }


//         try {

//             const response = await api.get(
//                 `/teacher-salary/${teacherId}/`
//             );


//             form.setFieldValue(
//                 "basic_salary",
//                 Number(
//                     response.data.salary || 0
//                 )
//             );

//         } catch (error) {

//             console.error(
//                 "SALARY FETCH ERROR:",
//                 error
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | TEACHER CHANGE
//     |--------------------------------------------------------------------------
//     */

//     const handleTeacherChange = (
//         teacherId
//     ) => {

//         setSelectedStaffType(
//             teacherId
//                 ? "teacher"
//                 : null
//         );


//         form.setFieldsValue({

//             teacher_id:
//                 teacherId || null,

//             support_staff_id:
//                 null,

//         });


//         const teacher =
//             teachers.find(
//                 (item) =>
//                     item.id === teacherId
//             );


//         if (teacher?.salary) {

//             form.setFieldValue(
//                 "basic_salary",
//                 Number(teacher.salary)
//             );

//         }


//         if (teacherId) {

//             fetchTeacherSalary(
//                 teacherId
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | SUPPORT STAFF CHANGE
//     |--------------------------------------------------------------------------
//     */

//     const handleSupportStaffChange = (
//         staffId
//     ) => {

//         setSelectedStaffType(
//             staffId
//                 ? "support_staff"
//                 : null
//         );


//         const staff =
//             supportStaff.find(
//                 (item) =>
//                     item.id === staffId
//             );


//         form.setFieldsValue({

//             support_staff_id:
//                 staffId || null,

//             teacher_id:
//                 null,

//             basic_salary:
//                 Number(
//                     staff?.salary || 0
//                 ),

//         });

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | SUBMIT PAYROLL
//     |--------------------------------------------------------------------------
//     */

//     const handleSubmit = async (
//         values
//     ) => {

//         if (
//             !values.teacher_id &&
//             !values.support_staff_id
//         ) {

//             message.error(
//                 "Please select a teacher or support staff."
//             );

//             return;

//         }


//         if (!selectedYear?.id) {

//             message.error(
//                 "Please select an academic year."
//             );

//             return;

//         }


//         setLoading(true);


//         try {

//             const payload = {

//                 teacher_id:
//                     values.teacher_id || null,

//                 support_staff_id:
//                     values.support_staff_id || null,

//                 month:
//                     values.month,

//                 basic_salary:
//                     Number(
//                         values.basic_salary || 0
//                     ),

//                 night_school_commission:
//                     Number(
//                         values.night_school_commission || 0
//                     ),

//                 salary_advance:
//                     Number(
//                         values.salary_advance || 0
//                     ),

//                 income_tax:
//                     Number(
//                         values.income_tax || 0
//                     ),

//                 social_security_tax:
//                     Number(
//                         values.social_security_tax || 0
//                     ),

//                 total_deductions:
//                     Number(
//                         values.total_deductions || 0
//                     ),

//             };


//             const response = editingPayroll

//                 ? await api.put(
//                     `/payrolls/${editingPayroll.id}/`,
//                     payload
//                 )

//                 : await api.post(
//                     "/payrolls/",
//                     payload
//                 );


//             if (editingPayroll) {

//                 setPayrolls(
//                     (previous) =>

//                         previous.map(
//                             (item) =>

//                                 item.id ===
//                                 editingPayroll.id

//                                     ? response.data

//                                     : item

//                         )

//                 );

//                 message.success(
//                     "Payroll updated successfully."
//                 );

//             } else {

//                 setPayrolls(
//                     (previous) => [

//                         response.data,

//                         ...previous,

//                     ]
//                 );

//                 message.success(
//                     "Payroll created successfully."
//                 );

//             }


//             closeModal();

//         } catch (error) {

//             console.error(
//                 "PAYROLL SUBMIT ERROR:",
//                 error.response?.data || error
//             );


//             Swal.fire({

//                 icon: "error",

//                 title: "Payroll Error",

//                 text: JSON.stringify(
//                     error.response?.data ||
//                     "Unable to save payroll."
//                 ),

//             });

//         } finally {

//             setLoading(false);

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | DELETE PAYROLL
//     |--------------------------------------------------------------------------
//     */

//     const handleDelete = async (
//         id
//     ) => {

//         const confirmation =
//             await Swal.fire({

//                 title:
//                     "Delete Payroll?",

//                 text:
//                     "This payroll record will be permanently deleted.",

//                 icon:
//                     "warning",

//                 showCancelButton:
//                     true,

//                 confirmButtonText:
//                     "Delete",

//                 confirmButtonColor:
//                     "#f97316",

//             });


//         if (
//             !confirmation.isConfirmed
//         ) {

//             return;

//         }


//         try {

//             await api.delete(
//                 `/payrolls/${id}/`
//             );


//             setPayrolls(
//                 (previous) =>

//                     previous.filter(
//                         (item) =>
//                             item.id !== id
//                     )

//             );


//             message.success(
//                 "Payroll deleted successfully."
//             );

//         } catch (error) {

//             console.error(
//                 error
//             );


//             message.error(
//                 "Failed to delete payroll."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | PRINT PAYROLL
//     |--------------------------------------------------------------------------
//     */

//     const handlePrint = (
//         payroll
//     ) => {

//         const newWindow =
//             window.open(
//                 "",
//                 "_blank"
//             );


//         if (!newWindow) {

//             return;

//         }


//         const staffName =
//             getStaffName(payroll);


//         newWindow.document.write(`

//             <html>

//                 <head>

//                     <title>
//                         Payroll - ${staffName}
//                     </title>

//                     <style>

//                         body {
//                             font-family: Arial, sans-serif;
//                             padding: 30px;
//                             color: #111;
//                         }

//                         h1 {
//                             text-align: center;
//                         }

//                         table {
//                             width: 100%;
//                             border-collapse: collapse;
//                             margin-top: 25px;
//                         }

//                         th,
//                         td {
//                             border: 1px solid #ddd;
//                             padding: 12px;
//                             text-align: left;
//                         }

//                         th {
//                             background: #111;
//                             color: white;
//                         }

//                         .total {
//                             font-weight: bold;
//                             font-size: 18px;
//                         }

//                     </style>

//                 </head>

//                 <body>

//                     <h1>
//                         Payroll Slip
//                     </h1>

//                     <p>
//                         <strong>Staff:</strong>
//                         ${staffName}
//                     </p>

//                     <p>
//                         <strong>Month:</strong>
//                         ${payroll.month}
//                     </p>

//                     <p>
//                         <strong>Academic Year:</strong>
//                         ${payroll.academic_year?.name || ""}
//                     </p>

//                     <table>

//                         <tr>
//                             <th>Basic Salary</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.basic_salary
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Bonus / Compensation</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.night_school_commission
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Salary Advance</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.salary_advance
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Income Tax</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.income_tax
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Social Security</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.social_security_tax
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Total Deductions</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.total_deductions
//                                 )}
//                             </td>
//                         </tr>

//                         <tr class="total">
//                             <th>Total Paid</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.total_paid
//                                 )}
//                             </td>
//                         </tr>

//                     </table>

//                 </body>

//             </html>

//         `);


//         newWindow.document.close();

//         newWindow.print();

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FILTERED PAYROLLS
//     |--------------------------------------------------------------------------
//     */

//     const filteredPayrolls =
//         useMemo(() => {

//             return payrolls.filter(
//                 (payroll) => {

//                     const staffName =
//                         getStaffName(payroll);


//                     const matchesSearch =
//                         !filters.search ||

//                         staffName
//                             .toLowerCase()
//                             .includes(
//                                 filters.search
//                                     .toLowerCase()
//                             );


//                     const matchesMonth =
//                         !filters.month ||

//                         payroll.month
//                             ?.toLowerCase() ===
//                         filters.month
//                             .toLowerCase();


//                     return (

//                         matchesSearch &&

//                         matchesMonth

//                     );

//                 }
//             );

//         }, [
//             payrolls,
//             filters,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | STATISTICS
//     |--------------------------------------------------------------------------
//     */

//     const statistics =
//         useMemo(() => {

//             const totalPayroll =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.total_paid || 0
//                         ),

//                     0
//                 );


//             const totalDeductions =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.total_deductions || 0
//                         ),

//                     0
//                 );


//             const totalAdvances =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.salary_advance || 0
//                         ),

//                     0
//                 );


//             return {

//                 totalPayroll,

//                 totalDeductions,

//                 totalAdvances,

//                 staffCount:
//                     filteredPayrolls.length,

//             };

//         }, [
//             filteredPayrolls,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | CHART DATA
//     |--------------------------------------------------------------------------
//     */

//     const chartData =
//         useMemo(() => {

//             const grouped =
//                 filteredPayrolls.reduce(
//                     (
//                         accumulator,
//                         item
//                     ) => {

//                         const key =
//                             item.month;


//                         if (
//                             !accumulator[key]
//                         ) {

//                             accumulator[key] = {

//                                 month:
//                                     key,

//                                 total:
//                                     0,

//                             };

//                         }


//                         accumulator[key].total +=
//                             Number(
//                                 item.total_paid || 0
//                             );


//                         return accumulator;

//                     },

//                     {}
//                 );


//             return Object.values(
//                 grouped
//             );

//         }, [
//             filteredPayrolls,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | TABLE COLUMNS
//     |--------------------------------------------------------------------------
//     */

//     const columns = [

//         {

//             title:
//                 "Staff Name",

//             key:
//                 "staff",

//             fixed:
//                 "left",

//             width:
//                 220,

//             render:
//                 (_, record) => (

//                     <div className="staff-cell">

//                         <div className="staff-avatar">

//                             {getStaffName(
//                                 record
//                             )
//                                 .charAt(0)
//                                 .toUpperCase()}

//                         </div>

//                         <div>

//                             <Text
//                                 strong
//                             >

//                                 {getStaffName(
//                                     record
//                                 )}

//                             </Text>

//                             <br />

//                             <Text
//                                 type="secondary"
//                                 className="staff-type"
//                             >

//                                 {record.teacher
//                                     ? "Teacher"
//                                     : "Support Staff"}

//                             </Text>

//                         </div>

//                     </div>

//                 ),

//         },


//         {

//             title:
//                 "Month",

//             dataIndex:
//                 "month",

//             key:
//                 "month",

//             width:
//                 120,

//             render:
//                 (month) => (

//                     <Tag
//                         color="orange"
//                     >

//                         {month}

//                     </Tag>

//                 ),

//         },


//         {

//             title:
//                 "Academic Year",

//             key:
//                 "academic_year",

//             width:
//                 150,

//             render:
//                 (_, record) =>

//                     record.academic_year?.name || "-",

//         },


//         {

//             title:
//                 "Basic Salary",

//             dataIndex:
//                 "basic_salary",

//             key:
//                 "basic_salary",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Bonus",

//             dataIndex:
//                 "night_school_commission",

//             key:
//                 "bonus",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Deductions",

//             dataIndex:
//                 "total_deductions",

//             key:
//                 "deductions",

//             width:
//                 140,

//             render:
//                 (value) => (

//                     <Text
//                         type="danger"
//                         strong
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Advance",

//             dataIndex:
//                 "salary_advance",

//             key:
//                 "advance",

//             width:
//                 140,

//             render:
//                 (value) => (

//                     <Text
//                         type="danger"
//                         strong
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Income Tax",

//             dataIndex:
//                 "income_tax",

//             key:
//                 "income_tax",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Social Security",

//             dataIndex:
//                 "social_security_tax",

//             key:
//                 "social_security",

//             width:
//                 150,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Total Paid",

//             dataIndex:
//                 "total_paid",

//             key:
//                 "total_paid",

//             width:
//                 150,

//             render:
//                 (value) => (

//                     <Text
//                         strong
//                         className="total-paid"
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Actions",

//             key:
//                 "actions",

//             fixed:
//                 "right",

//             width:
//                 160,

//             render:
//                 (_, record) => (

//                     <Space>

//                         <Button

//                             type="text"

//                             icon={
//                                 <EditOutlined />
//                             }

//                             onClick={() =>
//                                 openEditModal(
//                                     record
//                                 )
//                             }

//                         />

//                         <Button

//                             type="text"

//                             danger

//                             icon={
//                                 <DeleteOutlined />
//                             }

//                             onClick={() =>
//                                 handleDelete(
//                                     record.id
//                                 )
//                             }

//                         />

//                         <Button

//                             type="text"

//                             icon={
//                                 <PrinterOutlined />
//                             }

//                             onClick={() =>
//                                 handlePrint(
//                                     record
//                                 )
//                             }

//                         />

//                     </Space>

//                 ),

//         },

//     ];


//     return (

//         <Layout
//             className="payroll-page"
//         >

//             <Content
//                 className="payroll-content"
//             >

//                 {/* HEADER */}

//                 <div
//                     className="payroll-header"
//                 >

//                     <div>

//                         <Text
//                             className="eyebrow"
//                         >

//                             FINANCE / PAYROLL

//                         </Text>


//                         <Title
//                             level={2}
//                             className="page-title"
//                         >

//                             Staff Payroll Management

//                         </Title>


//                         <Text
//                             type="secondary"
//                         >

//                             Manage staff compensation, deductions,
//                             salary advances and payroll reporting.

//                         </Text>

//                     </div>


//                     <div
//                         className="year-badge"
//                     >

//                         <Text>
//                             Academic Year
//                         </Text>

//                         <strong>
//                             {selectedYear?.name || "Not Selected"}
//                         </strong>

//                     </div>

//                 </div>


//                 {/* STATISTICS */}

//                 <Row
//                     gutter={[
//                         16,
//                         16,
//                     ]}
//                     className="stats-row"
//                 >

//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card orange-card"
//                         >

//                             <Statistic

//                                 title="Total Payroll"

//                                 value={
//                                     statistics.totalPayroll
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <DollarOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card dark-card"
//                         >

//                             <Statistic

//                                 title="Staff Records"

//                                 value={
//                                     statistics.staffCount
//                                 }

//                                 prefix={
//                                     <TeamOutlined />
//                                 }

//                             />

//                             <TeamOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card light-card"
//                         >

//                             <Statistic

//                                 title="Total Deductions"

//                                 value={
//                                     statistics.totalDeductions
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <RiseOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card black-card"
//                         >

//                             <Statistic

//                                 title="Salary Advances"

//                                 value={
//                                     statistics.totalAdvances
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <WalletOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>

//                 </Row>


//                 {/* SALARY ADVANCE */}

//                 {showSalaryAdvance ? (

//                     <Card>

//                         <Button

//                             icon={
//                                 <ReloadOutlined />
//                             }

//                             onClick={() =>
//                                 setShowSalaryAdvance(
//                                     false
//                                 )
//                             }

//                             className="back-button"

//                         >

//                             Back To Payroll

//                         </Button>


//                         <SalaryAdvance />

//                     </Card>

//                 ) : (

//                     <>

//                         {/* TOOLBAR */}

//                         <Card
//                             className="toolbar-card"
//                         >

//                             <Row
//                                 gutter={[
//                                     12,
//                                     12,
//                                 ]}
//                                 align="middle"
//                             >

//                                 <Col
//                                     xs={24}
//                                     md={8}
//                                 >

//                                     <Input

//                                         size="large"

//                                         prefix={
//                                             <SearchOutlined />
//                                         }

//                                         placeholder="Search staff name..."

//                                         value={
//                                             filters.search
//                                         }

//                                         onChange={(
//                                             event
//                                         ) =>

//                                             setFilters(
//                                                 (
//                                                     previous
//                                                 ) => ({

//                                                     ...previous,

//                                                     search:
//                                                         event
//                                                             .target
//                                                             .value,

//                                                 })
//                                             )

//                                         }

//                                         allowClear

//                                     />

//                                 </Col>


//                                 <Col
//                                     xs={24}
//                                     sm={12}
//                                     md={5}
//                                 >

//                                     <Select

//                                         size="large"

//                                         placeholder="Filter by month"

//                                         value={
//                                             filters.month ||
//                                             undefined
//                                         }

//                                         onChange={(
//                                             value
//                                         ) =>

//                                             setFilters(
//                                                 (
//                                                     previous
//                                                 ) => ({

//                                                     ...previous,

//                                                     month:
//                                                         value ||
//                                                         "",

//                                                 })
//                                             )

//                                         }

//                                         allowClear

//                                         options={
//                                             MONTHS.map(
//                                                 (
//                                                     month
//                                                 ) => ({

//                                                     label:
//                                                         month,

//                                                     value:
//                                                         month,

//                                                 })
//                                             )
//                                         }

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                     />

//                                 </Col>


//                                 <Col
//                                     xs={24}
//                                     sm={12}
//                                     md={11}
//                                 >

//                                     <Space
//                                         wrap
//                                         className="toolbar-actions"
//                                     >

//                                         <Button

//                                             icon={
//                                                 <WalletOutlined />
//                                             }

//                                             onClick={() =>
//                                                 setShowSalaryAdvance(
//                                                     true
//                                                 )
//                                             }

//                                         >

//                                             Salary Advances

//                                         </Button>


//                                         <Button

//                                             type="primary"

//                                             icon={
//                                                 <PlusOutlined />
//                                             }

//                                             onClick={
//                                                 openCreateModal
//                                             }

//                                         >

//                                             Create Payroll

//                                         </Button>


//                                         <PDFDownloadLink

//                                             document={

//                                                 <PayrollSlipGenerationPDF

//                                                     payrolls={
//                                                         filteredPayrolls
//                                                     }

//                                                     month={
//                                                         filters.month
//                                                     }

//                                                     academicYear={
//                                                         selectedYear?.name
//                                                     }

//                                                 />

//                                             }

//                                             fileName={

//                                                 `Payslips_${

//                                                     filters.month ||
//                                                     "All_Months"

//                                                 }_${

//                                                     selectedYear?.name ||
//                                                     "Academic_Year"

//                                                 }.pdf`

//                                             }

//                                         >

//                                             {({
//                                                 loading,
//                                             }) => (

//                                                 <Button

//                                                     icon={
//                                                         <FilePdfOutlined />
//                                                     }

//                                                     danger

//                                                 >

//                                                     {loading

//                                                         ? "Generating..."

//                                                         : "Download PDF"}

//                                                 </Button>

//                                             )}

//                                         </PDFDownloadLink>


//                                     </Space>

//                                 </Col>

//                             </Row>

//                         </Card>


//                         {/* TABLE */}

//                         <Card
//                             className="table-card"
//                         >

//                             <Table

//                                 rowKey="id"

//                                 loading={
//                                     tableLoading
//                                 }

//                                 columns={
//                                     columns
//                                 }

//                                 dataSource={
//                                     filteredPayrolls
//                                 }

//                                 scroll={{
//                                     x:
//                                         1650,
//                                 }}

//                                 pagination={{
//                                     pageSize:
//                                         10,

//                                     showSizeChanger:
//                                         true,

//                                     pageSizeOptions:
//                                         [
//                                             "10",
//                                             "20",
//                                             "50",
//                                             "100",
//                                         ],

//                                     showTotal:
//                                         (
//                                             total
//                                         ) =>

//                                             `${total} payroll records`,

//                                 }}

//                                 locale={{

//                                     emptyText: (

//                                         <Empty

//                                             description={
//                                                 selectedYear?.id

//                                                     ? "No payroll records found"

//                                                     : "Please select an academic year"

//                                             }

//                                         />

//                                     ),

//                                 }}

//                             />

//                         </Card>


//                         {/* CHART */}

//                         <Card
//                             className="chart-card"
//                         >

//                             <div
//                                 className="section-heading"
//                             >

//                                 <div>

//                                     <Title
//                                         level={4}
//                                     >

//                                         Monthly Payroll Summary

//                                     </Title>

//                                     <Text
//                                         type="secondary"
//                                     >

//                                         Total payroll paid by month

//                                     </Text>

//                                 </div>

//                             </div>


//                             <div
//                                 className="chart-wrapper"
//                             >

//                                 <ResponsiveContainer
//                                     width="100%"
//                                     height="100%"
//                                 >

//                                     <BarChart
//                                         data={
//                                             chartData
//                                         }
//                                     >

//                                         <XAxis
//                                             dataKey="month"
//                                         />

//                                         <YAxis />

//                                         <Tooltip />

//                                         <Bar
//                                             dataKey="total"
//                                             fill="#f97316"
//                                             radius={[
//                                                 8,
//                                                 8,
//                                                 0,
//                                                 0,
//                                             ]}
//                                         />

//                                     </BarChart>

//                                 </ResponsiveContainer>

//                             </div>

//                         </Card>

//                     </>

//                 )}


//                 {/* CREATE / EDIT MODAL */}

//                 <Modal

//                     open={
//                         openModal
//                     }

//                     title={

//                         <div
//                             className="modal-title"
//                         >

//                             <div
//                                 className="modal-title-icon"
//                             >

//                                 <DollarOutlined />

//                             </div>


//                             <div>

//                                 <Title
//                                     level={4}
//                                 >

//                                     {editingPayroll

//                                         ? "Edit Payroll"

//                                         : "Create Payroll"}

//                                 </Title>

//                                 <Text
//                                     type="secondary"
//                                 >

//                                     {selectedYear?.name}

//                                 </Text>

//                             </div>

//                         </div>

//                     }

//                     onCancel={
//                         closeModal
//                     }

//                     footer={null}

//                     width={
//                         850
//                     }

//                     centered

//                     destroyOnClose

//                 >

//                     <Alert

//                         message="Select only one staff member"

//                         description="A payroll record can belong to either a Teacher or Support Staff, not both."

//                         type="info"

//                         showIcon

//                         style={{
//                             marginBottom:
//                                 24,
//                         }}

//                     />


//                     <Form

//                         form={
//                             form
//                         }

//                         layout="vertical"

//                         onFinish={
//                             handleSubmit
//                         }

//                     >

//                         <Row
//                             gutter={[
//                                 16,
//                                 0,
//                             ]}
//                         >

//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item
//                                     label="Teacher"
//                                     name="teacher_id"
//                                 >

//                                     <Select

//                                         showSearch

//                                         allowClear

//                                         placeholder="Select teacher"

//                                         disabled={
//                                             selectedStaffType ===
//                                             "support_staff"
//                                         }

//                                         optionFilterProp="label"

//                                         onChange={
//                                             handleTeacherChange
//                                         }

//                                         options={

//                                             teachers.map(
//                                                 (
//                                                     teacher
//                                                 ) => ({

//                                                     label:
//                                                         teacher.full_name,

//                                                     value:
//                                                         teacher.id,

//                                                 })
//                                             )

//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item
//                                     label="Support Staff"
//                                     name="support_staff_id"
//                                 >

//                                     <Select

//                                         showSearch

//                                         allowClear

//                                         placeholder="Select support staff"

//                                         disabled={
//                                             selectedStaffType ===
//                                             "teacher"
//                                         }

//                                         optionFilterProp="label"

//                                         onChange={
//                                             handleSupportStaffChange
//                                         }

//                                         options={

//                                             supportStaff.map(
//                                                 (
//                                                     staff
//                                                 ) => ({

//                                                     label:
//                                                         staff.full_name,

//                                                     value:
//                                                         staff.id,

//                                                 })
//                                             )

//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Month"

//                                     name="month"

//                                     rules={[
//                                         {
//                                             required:
//                                                 true,

//                                             message:
//                                                 "Please select a month.",

//                                         },
//                                     ]}

//                                 >

//                                     <Select

//                                         placeholder="Select month"

//                                         options={

//                                             MONTHS.map(
//                                                 (
//                                                     month
//                                                 ) => ({

//                                                     label:
//                                                         month,

//                                                     value:
//                                                         month,

//                                                 })
//                                             )

//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Basic Salary"

//                                     name="basic_salary"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         readOnly

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Bonus / Compensation"

//                                     name="night_school_commission"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Salary Advance"

//                                     name="salary_advance"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Income Tax"

//                                     name="income_tax"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Social Security Tax"

//                                     name="social_security_tax"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>

//                         </Row>


//                         <div
//                             className="modal-footer"
//                         >

//                             <Button
//                                 onClick={
//                                     closeModal
//                                 }
//                             >

//                                 Cancel

//                             </Button>


//                             <Button

//                                 type="primary"

//                                 htmlType="submit"

//                                 loading={
//                                     loading
//                                 }

//                             >

//                                 {editingPayroll

//                                     ? "Update Payroll"

//                                     : "Create Payroll"}

//                             </Button>

//                         </div>

//                     </Form>

//                 </Modal>

//             </Content>

//         </Layout>

//     );

// }


// export default Payroll;







// Old Useful Code for Production purpose

// import React, { useEffect, useState } from "react";

// import {
//   Box,
//   Button,
//   Modal,
//   Fade,
//   Backdrop,
//   TextField,
//   Typography,
//   CircularProgress,
//   Table,
//   TableBody,
//   TableCell,
//   TableHead,
//   TableRow,
//   TableContainer,
//   Paper,
//   IconButton,
//   Autocomplete,
// } from "@mui/material";

// import Grid from "@mui/material/Grid";

// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";

// import DeleteIcon from "@mui/icons-material/Delete";
// import EditIcon from "@mui/icons-material/Edit";
// import PrintIcon from "@mui/icons-material/Print";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// import SalaryAdvance from "./SalaryAdvance";

// import { useAcademicYear } from "../../AcademicYearContext";

// import Swal from "sweetalert2";

// import api from "../../api/api";

// import { PDFDownloadLink } from "@react-pdf/renderer";

// import PayrollSlipGenerationPDF from "./PayrollSlipGenerationPDF";


// const modalStyle = {
//   position: "absolute",
//   top: "50%",
//   left: "50%",
//   transform: "translate(-50%, -50%)",

//   width: {
//     xs: "95%",
//     sm: 600,
//     md: 800,
//   },

//   bgcolor: "white",

//   borderRadius: 2,

//   boxShadow: 24,

//   p: 4,

//   maxHeight: "90vh",

//   overflowY: "auto",
// };


// const MONTHS = [
//   "January",
//   "February",
//   "March",
//   "April",
//   "May",
//   "June",
//   "July",
//   "August",
//   "September",
//   "October",
//   "November",
//   "December",
// ];


// const initialFormData = {
//   teacher_id: null,

//   support_staff_id: null,

//   month: "",

//   basic_salary: "",

//   night_school_commission: "0.0",

//   total_deductions: "0.0",

//   salary_advance: "0.0",

//   income_tax: "0.0",

//   social_security_tax: "0.0",
// };


// function Payroll() {

//   const [open, setOpen] = useState(false);

//   const [payrolls, setPayrolls] = useState([]);

//   const [teachers, setTeachers] = useState([]);

//   const [supportStaff, setSupportStaff] = useState([]);

//   const [loading, setLoading] = useState(false);

//   const [editingPayroll, setEditingPayroll] = useState(null);

//   const [showSalaryAdvance, setShowSalaryAdvance] = useState(false);

//   const [filters, setFilters] = useState({
//     search: "",
//     month: "",
//   });

//   const [formData, setFormData] = useState(initialFormData);

//   const { selectedYear } = useAcademicYear();


//   /*
//   |--------------------------------------------------------------------------
//   | FETCH PAYROLLS
//   |--------------------------------------------------------------------------
//   */

//   const fetchPayrolls = async () => {

//     try {

//       const response = await api.get(
//         "/payrolls/",
//         {
//           params: {
//             academic_year: selectedYear?.id,
//           },
//         }
//       );

//       setPayrolls(
//         Array.isArray(response.data)
//           ? response.data
//           : response.data.results || []
//       );

//     } catch (error) {

//       console.error(error);

//       Swal.fire(
//         "Error",
//         "Failed to load payrolls.",
//         "error"
//       );

//     }

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | FETCH TEACHERS
//   |--------------------------------------------------------------------------
//   */

//   const fetchTeachers = async () => {

//     try {

//       const response = await api.get(
//         "/api/users/teacher-data/"
//       );

//       setTeachers(
//         Array.isArray(response.data)
//           ? response.data
//           : response.data.results || []
//       );

//     } catch (error) {

//       console.error(error);

//       Swal.fire(
//         "Error",
//         "Failed to load teachers.",
//         "error"
//       );

//     }

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | FETCH SUPPORT STAFF
//   |--------------------------------------------------------------------------
//   */

//   const fetchSupportStaff = async () => {

//     try {

//       const response = await api.get(
//         "/supportstaff/"
//       );

//       setSupportStaff(
//         Array.isArray(response.data)
//           ? response.data
//           : response.data.results || []
//       );

//     } catch (error) {

//       console.error(error);

//       Swal.fire(
//         "Error",
//         "Failed to load support staff.",
//         "error"
//       );

//     }

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | EFFECTS
//   |--------------------------------------------------------------------------
//   */

//   useEffect(() => {

//     if (selectedYear) {

//       fetchPayrolls();

//     }

//   }, [selectedYear]);


//   useEffect(() => {

//     fetchTeachers();

//     fetchSupportStaff();

//   }, []);


//   /*
//   |--------------------------------------------------------------------------
//   | RESET FORM
//   |--------------------------------------------------------------------------
//   */

//   const resetForm = () => {

//     setFormData({
//       ...initialFormData,
//     });

//     setEditingPayroll(null);

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | CLOSE MODAL
//   |--------------------------------------------------------------------------
//   */

//   const handleCloseModal = () => {

//     setOpen(false);

//     resetForm();

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | SUBMIT PAYROLL
//   |--------------------------------------------------------------------------
//   */

//   const handleSubmit = async (event) => {

//     event.preventDefault();


//     /*
//     |--------------------------------------------------------------------------
//     | VALIDATE STAFF SELECTION
//     |--------------------------------------------------------------------------
//     */

//     if (
//       !formData.teacher_id &&
//       !formData.support_staff_id
//     ) {

//       Swal.fire(
//         "Error",
//         "Please select either a Teacher or a Support Staff.",
//         "error"
//       );

//       return;

//     }


//     if (
//       formData.teacher_id &&
//       formData.support_staff_id
//     ) {

//       Swal.fire(
//         "Error",
//         "Please select only one: Teacher or Support Staff.",
//         "error"
//       );

//       return;

//     }


//     if (!formData.month) {

//       Swal.fire(
//         "Error",
//         "Please select a Month.",
//         "error"
//       );

//       return;

//     }


//     if (!selectedYear?.id) {

//       Swal.fire(
//         "Error",
//         "Please select an Academic Year.",
//         "error"
//       );

//       return;

//     }


//     setLoading(true);


//     try {


//       /*
//       |--------------------------------------------------------------------------
//       | IMPORTANT
//       |
//       | Serializer expects:
//       |
//       | teacher_id
//       | support_staff_id
//       |
//       | NOT support_staff_name
//       |--------------------------------------------------------------------------
//       */

//       const payload = {

//         teacher_id:
//           formData.teacher_id || null,

//         support_staff_id:
//           formData.support_staff_id || null,

//         month:
//           formData.month,

//         basic_salary:
//           parseFloat(
//             formData.basic_salary
//           ) || 0,

//         night_school_commission:
//           parseFloat(
//             formData.night_school_commission
//           ) || 0,

//         salary_advance:
//           parseFloat(
//             formData.salary_advance
//           ) || 0,

//         income_tax:
//           parseFloat(
//             formData.income_tax
//           ) || 0,

//         social_security_tax:
//           parseFloat(
//             formData.social_security_tax
//           ) || 0,

//         total_deductions:
//           parseFloat(
//             formData.total_deductions
//           ) || 0,

//       };


//       console.log(
//         "PAYROLL PAYLOAD:",
//         payload
//       );


//       const endpoint = editingPayroll

//         ? `/payrolls/${editingPayroll.id}/`

//         : "/payrolls/";


//       const response = editingPayroll

//         ? await api.put(
//             endpoint,
//             payload
//           )

//         : await api.post(
//             endpoint,
//             payload
//           );


//       if (editingPayroll) {

//         setPayrolls((previous) =>

//           previous.map((payroll) =>

//             payroll.id === editingPayroll.id

//               ? response.data

//               : payroll

//           )

//         );

//       } else {

//         setPayrolls((previous) => [

//           response.data,

//           ...previous,

//         ]);

//       }


//       Swal.fire({

//         icon: "success",

//         title: editingPayroll

//           ? "Payroll Updated"

//           : "Payroll Created",

//         timer: 2000,

//         showConfirmButton: false,

//       });


//       handleCloseModal();


//     } catch (error) {

//       console.error(
//         "PAYROLL ERROR:",
//         error.response?.data || error
//       );


//       Swal.fire(

//         "Error",

//         JSON.stringify(
//           error.response?.data ||
//           "Submit failed."
//         ),

//         "error"

//       );

//     } finally {

//       setLoading(false);

//     }

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | EDIT PAYROLL
//   |--------------------------------------------------------------------------
//   */

//   const handleEdit = (payroll) => {

//     setEditingPayroll(payroll);


//     setFormData({

//       teacher_id:
//         payroll.teacher?.id || null,

//       /*
//       |--------------------------------------------------------------------------
//       | IMPORTANT
//       |
//       | Serializer returns:
//       |
//       | support_staff: {
//       |   id,
//       |   full_name
//       | }
//       |--------------------------------------------------------------------------
//       */

//       support_staff_id:
//         payroll.support_staff?.id || null,

//       month:
//         payroll.month || "",

//       basic_salary:
//         payroll.basic_salary || "",

//       night_school_commission:
//         payroll.night_school_commission || "0.0",

//       total_deductions:
//         payroll.total_deductions || "0.0",

//       salary_advance:
//         payroll.salary_advance || "0.0",

//       income_tax:
//         payroll.income_tax || "0.0",

//       social_security_tax:
//         payroll.social_security_tax || "0.0",

//     });


//     setOpen(true);

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | DELETE PAYROLL
//   |--------------------------------------------------------------------------
//   */

//   const handleDelete = async (id) => {

//     const confirmation = await Swal.fire({

//       title: "Are you sure?",

//       text:
//         "This payroll record will be permanently deleted!",

//       icon: "warning",

//       showCancelButton: true,

//       confirmButtonText:
//         "Yes, delete it!",

//     });


//     if (!confirmation.isConfirmed) {

//       return;

//     }


//     try {

//       await api.delete(
//         `/payrolls/${id}/`
//       );


//       setPayrolls((previous) =>

//         previous.filter(
//           (payroll) =>
//             payroll.id !== id
//         )

//       );


//       Swal.fire(

//         "Deleted!",

//         "Payroll record has been removed.",

//         "success"

//       );

//     } catch (error) {

//       console.error(error);


//       Swal.fire(

//         "Error",

//         "Failed to delete payroll.",

//         "error"

//       );

//     }

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | PRINT
//   |--------------------------------------------------------------------------
//   */

//   const handlePrint = (payroll) => {

//     const newWindow =
//       window.open(
//         "",
//         "_blank"
//       );


//     if (!newWindow) {

//       return;

//     }


//     const staffName =

//       payroll.teacher?.full_name ||

//       payroll.support_staff?.full_name ||

//       "";


//     newWindow.document.write(`

//       <html>

//         <head>

//           <title>
//             Payroll - ${staffName}
//           </title>

//           <style>

//             body {
//               font-family: Arial, sans-serif;
//               padding: 30px;
//             }

//             h1 {
//               text-align: center;
//             }

//             table {
//               width: 100%;
//               border-collapse: collapse;
//               margin-top: 20px;
//             }

//             th,
//             td {
//               border: 1px solid #ddd;
//               padding: 10px;
//               text-align: left;
//             }

//             th {
//               background: #f2f2f2;
//             }

//           </style>

//         </head>

//         <body>

//           <h1>
//             Payroll Slip
//           </h1>

//           <p>
//             <strong>Staff Name:</strong>
//             ${staffName}
//           </p>

//           <p>
//             <strong>Month:</strong>
//             ${payroll.month}
//           </p>

//           <p>
//             <strong>Academic Year:</strong>
//             ${payroll.academic_year?.name || ""}
//           </p>

//           <table>

//             <tr>
//               <th>Basic Salary</th>
//               <td>$${parseFloat(
//                 payroll.basic_salary || 0
//               ).toFixed(2)}</td>
//             </tr>

//             <tr>
//               <th>Bonus / Compensation</th>
//               <td>$${parseFloat(
//                 payroll.night_school_commission || 0
//               ).toFixed(2)}</td>
//             </tr>

//             <tr>
//               <th>Salary Advance</th>
//               <td>$${parseFloat(
//                 payroll.salary_advance || 0
//               ).toFixed(2)}</td>
//             </tr>

//             <tr>
//               <th>Income Tax</th>
//               <td>$${parseFloat(
//                 payroll.income_tax || 0
//               ).toFixed(2)}</td>
//             </tr>

//             <tr>
//               <th>Social Security Tax</th>
//               <td>$${parseFloat(
//                 payroll.social_security_tax || 0
//               ).toFixed(2)}</td>
//             </tr>

//             <tr>
//               <th>Total Deductions</th>
//               <td>$${parseFloat(
//                 payroll.total_deductions || 0
//               ).toFixed(2)}</td>
//             </tr>

//             <tr>
//               <th>Total Paid</th>
//               <td>
//                 <strong>
//                   $${parseFloat(
//                     payroll.total_paid || 0
//                   ).toFixed(2)}
//                 </strong>
//               </td>
//             </tr>

//           </table>

//         </body>

//       </html>

//     `);


//     newWindow.document.close();

//     newWindow.print();

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | MONEY FORMATTER
//   |--------------------------------------------------------------------------
//   */

//   const formatMoney = (value) => {

//     const number =
//       parseFloat(value || 0);


//     return `$${number.toFixed(2)}`;

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | FILTERED PAYSLIPS
//   |--------------------------------------------------------------------------
//   */

//   const getFilteredPayslips = () => {

//     return payrolls.filter(

//       (payroll) =>

//         (

//           !filters.month ||

//           payroll.month?.toLowerCase() ===

//             filters.month.toLowerCase()

//         )

//         &&

//         payroll.academic_year?.id ===

//           selectedYear?.id

//     );

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | CHART DATA
//   |--------------------------------------------------------------------------
//   */

//   const chartData = Object.values(

//     payrolls.reduce(

//       (accumulator, item) => {

//         const key =

//           `${item.month} ${item.academic_year?.name || ""}`;


//         if (!accumulator[key]) {

//           accumulator[key] = {

//             monthYear: key,

//             total: 0,

//           };

//         }


//         accumulator[key].total +=

//           parseFloat(
//             item.total_paid || 0
//           );


//         return accumulator;

//       },

//       {}

//     )

//   );


//   /*
//   |--------------------------------------------------------------------------
//   | TABLE FILTER
//   |--------------------------------------------------------------------------
//   */

//   const filteredPayrolls = payrolls.filter(

//     (payroll) => {


//       const staffName =

//         payroll.teacher?.full_name ||

//         payroll.support_staff?.full_name ||

//         "";


//       const matchesSearch =

//         !filters.search ||

//         staffName

//           .toLowerCase()

//           .includes(

//             filters.search.toLowerCase()

//           );


//       const matchesMonth =

//         !filters.month ||

//         payroll.month?.toLowerCase() ===

//           filters.month.toLowerCase();


//       return (

//         matchesSearch &&

//         matchesMonth

//       );

//     }

//   );


//   return (

//     <Box p={3}>


//       {/* HEADER */}

//       <Typography
//         variant="h5"
//         mb={2}
//       >

//         Staff Payroll Management

//       </Typography>


//       <Box mb={3}>

//         <Typography
//           variant="subtitle2"
//         >

//           Academic Year

//         </Typography>


//         <Typography
//           variant="h5"
//         >

//           {selectedYear?.name || ""}

//         </Typography>

//       </Box>


//       {/* ACTION BAR */}

//       <Box

//         display="flex"

//         gap={2}

//         flexWrap="wrap"

//         mb={2}

//         alignItems="center"

//       >


//         {showSalaryAdvance ? (


//           <Button

//             variant="outlined"

//             color="primary"

//             onClick={() =>

//               setShowSalaryAdvance(false)

//             }

//           >

//             Back To Payroll

//           </Button>


//         ) : (


//           <>


//             <Button

//               variant="contained"

//               color="success"

//               onClick={() =>

//                 setShowSalaryAdvance(true)

//               }

//             >

//               View Salary Advances

//             </Button>


//             <TextField

//               label="Search Name"

//               value={
//                 filters.search
//               }

//               onChange={(event) =>

//                 setFilters(

//                   (previous) => ({

//                     ...previous,

//                     search:
//                       event.target.value,

//                   })

//                 )

//               }

//             />


//             <Autocomplete

//               options={MONTHS}

//               value={filters.month}

//               onChange={(event, value) =>

//                 setFilters(

//                   (previous) => ({

//                     ...previous,

//                     month: value || "",

//                   })

//                 )

//               }

//               renderInput={(params) =>

//                 <TextField

//                   {...params}

//                   label="Month"

//                 />

//               }

//               sx={{
//                 minWidth: 160,
//               }}

//             />


//             <Button

//               variant="contained"

//               onClick={() => {

//                 resetForm();

//                 setOpen(true);

//               }}

//             >

//               Create Payroll

//             </Button>


//             <PDFDownloadLink

//               document={

//                 <PayrollSlipGenerationPDF

//                   payrolls={

//                     getFilteredPayslips()

//                   }

//                   month={

//                     filters.month

//                   }

//                   academicYear={

//                     selectedYear?.name

//                   }

//                 />

//               }

//               fileName={

//                 `Payslips_${

//                   filters.month

//                 }_${

//                   selectedYear?.name

//                 }.pdf`

//               }

//             >

//               {({ loading }) => (

//                 <Button

//                   variant="outlined"

//                   color="secondary"

//                   startIcon={

//                     <PictureAsPdfIcon />

//                   }

//                 >

//                   {loading

//                     ? "Generating PDF..."

//                     : "Download Payslips + Signing Page"}

//                 </Button>

//               )}

//             </PDFDownloadLink>


//           </>

//         )}

//       </Box>


//       {/* SALARY ADVANCE */}

//       {showSalaryAdvance ? (


//         <Box>

//           <SalaryAdvance />

//         </Box>


//       ) : (


//         <>


//           {/* PAYROLL TABLE */}

//           <TableContainer
//             component={Paper}
//           >

//             <Table>

//               <TableHead>

//                 <TableRow>

//                   <TableCell>
//                     Staff Name
//                   </TableCell>

//                   <TableCell>
//                     Month
//                   </TableCell>

//                   <TableCell>
//                     Academic Year
//                   </TableCell>

//                   <TableCell>
//                     Basic
//                   </TableCell>

//                   <TableCell>
//                     Bonus / Compensation
//                   </TableCell>

//                   <TableCell>
//                     Total Deductions
//                   </TableCell>

//                   <TableCell>
//                     Salary Advance
//                   </TableCell>

//                   <TableCell>
//                     Income Tax
//                   </TableCell>

//                   <TableCell>
//                     Social Security
//                   </TableCell>

//                   <TableCell>
//                     Total
//                   </TableCell>

//                   <TableCell>
//                     Actions
//                   </TableCell>

//                 </TableRow>

//               </TableHead>


//               <TableBody>

//                 {filteredPayrolls.map(

//                   (item) => {


//                     const staffName =

//                       item.teacher?.full_name ||

//                       item.support_staff?.full_name ||

//                       "";


//                     return (

//                       <TableRow

//                         key={item.id}

//                       >

//                         <TableCell>

//                           {staffName}

//                         </TableCell>


//                         <TableCell>

//                           {item.month}

//                         </TableCell>


//                         <TableCell>

//                           {item.academic_year?.name}

//                         </TableCell>


//                         <TableCell>

//                           {formatMoney(
//                             item.basic_salary
//                           )}

//                         </TableCell>


//                         <TableCell>

//                           {formatMoney(
//                             item.night_school_commission
//                           )}

//                         </TableCell>


//                         <TableCell

//                           sx={{

//                             color: "red",

//                             fontWeight: "bold",

//                           }}

//                         >

//                           {formatMoney(
//                             item.total_deductions
//                           )}

//                         </TableCell>


//                         <TableCell

//                           sx={{

//                             color: "red",

//                             fontWeight: "bold",

//                           }}

//                         >

//                           {formatMoney(
//                             item.salary_advance
//                           )}

//                         </TableCell>


//                         <TableCell

//                           sx={{

//                             color: "red",

//                             fontWeight: "bold",

//                           }}

//                         >

//                           {formatMoney(
//                             item.income_tax
//                           )}

//                         </TableCell>


//                         <TableCell

//                           sx={{

//                             color: "red",

//                             fontWeight: "bold",

//                           }}

//                         >

//                           {formatMoney(
//                             item.social_security_tax
//                           )}

//                         </TableCell>


//                         <TableCell>

//                           <strong>

//                             {formatMoney(
//                               item.total_paid
//                             )}

//                           </strong>

//                         </TableCell>


//                         <TableCell>

//                           <IconButton

//                             onClick={() =>

//                               handleEdit(item)

//                             }

//                           >

//                             <EditIcon />

//                           </IconButton>


//                           <IconButton

//                             onClick={() =>

//                               handleDelete(
//                                 item.id
//                               )

//                             }

//                           >

//                             <DeleteIcon />

//                           </IconButton>


//                           <IconButton

//                             onClick={() =>

//                               handlePrint(item)

//                             }

//                           >

//                             <PrintIcon />

//                           </IconButton>

//                         </TableCell>


//                       </TableRow>

//                     );

//                   }

//                 )}


//                 {filteredPayrolls.length === 0 && (

//                   <TableRow>

//                     <TableCell

//                       colSpan={11}

//                       align="center"

//                     >

//                       No payroll records found.

//                     </TableCell>

//                   </TableRow>

//                 )}

//               </TableBody>

//             </Table>

//           </TableContainer>


//           {/* CHART */}

//           <Box

//             mt={4}

//             height={300}

//           >

//             <Typography

//               variant="h6"

//               mb={1}

//             >

//               Monthly Payroll Summary

//             </Typography>


//             <ResponsiveContainer

//               width="100%"

//               height="100%"

//             >

//               <BarChart

//                 data={chartData}

//               >

//                 <XAxis

//                   dataKey="monthYear"

//                 />

//                 <YAxis />

//                 <Tooltip />


//                 <Bar

//                   dataKey="total"

//                   fill="#1976d2"

//                 />

//               </BarChart>

//             </ResponsiveContainer>

//           </Box>


//         </>

//       )}


//       {/* PAYROLL MODAL */}

//       <Modal

//         open={open}

//         onClose={handleCloseModal}

//         closeAfterTransition

//         BackdropComponent={Backdrop}

//         BackdropProps={{

//           timeout: 400,

//         }}

//       >

//         <Fade in={open}>

//           <Box

//             sx={modalStyle}

//             component="form"

//             onSubmit={handleSubmit}

//           >


//             <Typography

//               variant="h6"

//               mb={2}

//             >

//               {editingPayroll

//                 ? "Edit Payroll"

//                 : "Create Payroll"}

//             </Typography>


//             <Grid

//               container

//               spacing={2}

//             >


//               {/* LEFT COLUMN */}

//               <Grid

//                 item

//                 xs={12}

//                 md={6}

//               >


//                 {/* TEACHER */}

//                 <Autocomplete

//                   options={teachers}

//                   getOptionLabel={(option) =>

//                     option.full_name || ""

//                   }

//                   value={

//                     teachers.find(

//                       (teacher) =>

//                         teacher.id ===

//                         formData.teacher_id

//                     ) || null

//                   }

//                   onChange={

//                     (event, newValue) => {


//                       setFormData(

//                         (previous) => ({

//                           ...previous,

//                           teacher_id:

//                             newValue?.id ||

//                             null,

//                           support_staff_id:

//                             null,

//                           basic_salary:

//                             newValue?.salary ||

//                             "",

//                         })

//                       );


//                       if (newValue?.id) {

//                         api.get(

//                           `/teacher-salary/${

//                             newValue.id

//                           }/`

//                         )

//                         .then((response) => {


//                           setFormData(

//                             (previous) => ({

//                               ...previous,

//                               basic_salary:

//                                 response.data.salary,

//                             })

//                           );

//                         })

//                         .catch((error) => {

//                           console.error(
//                             error
//                           );

//                         });

//                       }

//                     }

//                   }

//                   renderInput={(params) => (

//                     <TextField

//                       {...params}

//                       label="Select Teacher"

//                       margin="dense"

//                       disabled={

//                         !!formData.support_staff_id

//                       }

//                       fullWidth

//                     />

//                   )}

//                   clearOnEscape

//                 />


//                 {/* SUPPORT STAFF */}

//                 <Autocomplete

//                   options={supportStaff}

//                   getOptionLabel={(option) =>

//                     option.full_name || ""

//                   }

//                   value={

//                     supportStaff.find(

//                       (staff) =>

//                         staff.id ===

//                         formData.support_staff_id

//                     ) || null

//                   }

//                   onChange={

//                     (event, newValue) => {


//                       setFormData(

//                         (previous) => ({

//                           ...previous,

//                           support_staff_id:

//                             newValue?.id ||

//                             null,

//                           teacher_id:

//                             null,

//                           basic_salary:

//                             newValue?.salary ||

//                             "",

//                         })

//                       );

//                     }

//                   }

//                   renderInput={(params) => (

//                     <TextField

//                       {...params}

//                       label="Select Support Staff"

//                       margin="dense"

//                       disabled={

//                         !!formData.teacher_id

//                       }

//                       fullWidth

//                     />

//                   )}

//                   clearOnEscape

//                 />


//                 {/* MONTH */}

//                 <Autocomplete

//                   options={MONTHS}

//                   value={

//                     formData.month

//                   }

//                   onChange={

//                     (event, value) =>

//                       setFormData(

//                         (previous) => ({

//                           ...previous,

//                           month:

//                             value || "",

//                         })

//                       )

//                   }

//                   renderInput={(params) => (

//                     <TextField

//                       {...params}

//                       label="Month"

//                       margin="dense"

//                       required

//                       fullWidth

//                     />

//                   )}

//                   clearOnEscape

//                 />


//               </Grid>


//               {/* RIGHT COLUMN */}

//               <Grid

//                 item

//                 xs={12}

//                 md={6}

//               >


//                 <TextField

//                   fullWidth

//                   label="Salary Advance"

//                   type="number"

//                   value={

//                     formData.salary_advance

//                   }

//                   onChange={(event) =>

//                     setFormData(

//                       (previous) => ({

//                         ...previous,

//                         salary_advance:

//                           event.target.value,

//                       })

//                     )

//                   }

//                   margin="dense"

//                 />


//                 <TextField

//                   fullWidth

//                   label="Income Tax"

//                   type="number"

//                   value={

//                     formData.income_tax

//                   }

//                   onChange={(event) =>

//                     setFormData(

//                       (previous) => ({

//                         ...previous,

//                         income_tax:

//                           event.target.value,

//                       })

//                     )

//                   }

//                   margin="dense"

//                 />


//                 <TextField

//                   fullWidth

//                   label="Social Security Tax"

//                   type="number"

//                   value={

//                     formData.social_security_tax

//                   }

//                   onChange={(event) =>

//                     setFormData(

//                       (previous) => ({

//                         ...previous,

//                         social_security_tax:

//                           event.target.value,

//                       })

//                     )

//                   }

//                   margin="dense"

//                 />


//               </Grid>


//               {/* BONUS */}

//               <Grid

//                 item

//                 xs={12}

//                 md={6}

//               >

//                 <TextField

//                   fullWidth

//                   label="Bonus or Compensation"

//                   type="number"

//                   value={

//                     formData.night_school_commission

//                   }

//                   onChange={(event) =>

//                     setFormData(

//                       (previous) => ({

//                         ...previous,

//                         night_school_commission:

//                           event.target.value,

//                       })

//                     )

//                   }

//                   margin="dense"

//                 />

//               </Grid>


//               {/* BASIC SALARY */}

//               <Grid

//                 item

//                 xs={12}

//                 md={6}

//               >

//                 <TextField

//                   fullWidth

//                   label="Basic Salary"

//                   value={

//                     formData.basic_salary

//                   }

//                   margin="dense"

//                   InputProps={{

//                     readOnly: true,

//                   }}

//                   required

//                 />

//               </Grid>


//             </Grid>


//             {/* BUTTONS */}

//             <Box

//               mt={3}

//               display="flex"

//               justifyContent="space-between"

//               alignItems="center"

//             >


//               <Button

//                 onClick={handleCloseModal}

//               >

//                 Cancel

//               </Button>


//               <Button

//                 variant="contained"

//                 type="submit"

//                 disabled={loading}

//               >

//                 {loading ? (

//                   <CircularProgress

//                     size={24}

//                   />

//                 ) : editingPayroll ? (

//                   "Update Payroll"

//                 ) : (

//                   "Create Payroll"

//                 )}

//               </Button>


//             </Box>


//           </Box>

//         </Fade>

//       </Modal>


//     </Box>

//   );

// }


// export default Payroll;